import { useMemo } from 'react';
import {
  useBuildPCV2,
  useCartV2,
  useFrame,
  useServices,
  useTenantConfig,
} from 'contexts';
import { useDiscoverySearchV2, SearchV2Params } from '@teko/discovery/v2';
import { useHandlerUnderEighteenYearOld } from 'hooks/useHandlerUnderEighteenYearOld';
import { ProductCardProps } from '@teko/types/ecommerce';
import { t } from 'i18n';
import { isServerless } from 'helpers';
import { BUILD_PC_MODAL_PAGE_SIZE } from 'constants/index';

export const useBuildPcProducts = (
  params: SearchV2Params,
  onCloseModal: () => void,
  categoryName: string,
) => {
  const { discoveryServiceV2, discoveryHelper } = useServices();
  const { isDisplayUom, showNewProductGift } = useTenantConfig();
  const { frames } = useFrame();
  const { transformProductWhenUserUnder18 } = useHandlerUnderEighteenYearOld();
  const { addHardware } = useBuildPCV2();
  const cartV2 = useCartV2();

  const { result, ...rest } = useDiscoverySearchV2(
    discoveryServiceV2,
    {
      ...params,
      isNeedFeaturedProducts: false,
    },
    {
      transform: (data) => ({
        products: data.products?.map((product) =>
          discoveryHelper.cleanupDiscoveryV2SearchProduct(product, {
            showUom: isDisplayUom,
            frames,
            showNewProductGift,
            cartV2,
          }),
        ),
        filter: data.filter,
        pagination: {
          totalItems: data.total,
          totalPages: Math.ceil(data.total / BUILD_PC_MODAL_PAGE_SIZE),
        },
      }),
    },
  );

  const productList = useMemo(() => {
    return (result?.products || [])
      .map((p) => ({
        ...p,
        ctaButton: !p.contactButton
          ? ({
              children: t('Chọn'),
              variant: 'secondary',
              ...(isServerless()
                ? {
                    onClick: (e: React.MouseEvent) => {
                      e.preventDefault();
                      addHardware(categoryName, { sku: p.sku, quantity: 1 });
                      onCloseModal();
                    },
                  }
                : {}),
            } as ProductCardProps['ctaButton'])
          : undefined,
      }))
      .map(transformProductWhenUserUnder18);
  }, [
    JSON.stringify(result?.products),
    transformProductWhenUserUnder18,
    categoryName,
  ]);

  return {
    products: productList,
    filter: result?.filter,
    pagination: result?.pagination,
    ...rest,
  };
};
